import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Box, CheckCircle, ArrowRight, Shield, Layers, Phone, Star, Package } from "lucide-react";

const PRODUCT_LINES = [
  {
    gamma: "alta",
    name: "SERIE MIL®",
    tagline: "La más fuerte, eficiente y especializada del mercado",
    color: "#2E7D32",
    badge: "PREMIUM",
    ect: "40 lb/pulg",
    estiba: 8,
    material: "Cartón corrugado Blanco o Kraft, Flauta C",
    features: ["SUPER-ESTRUCTURA H®", "Sistema ID-VISUAL TC®", "Tapa DOBLE ancho", "Candados triples", "A.I.R. TC® reforzado"],
    products: [
      { model: "AM-4000", name: "Serie Mil Carta", size: "48 × 31 × 25 cm", weight: "20 kg con docs", format: "Carta" },
      { model: "AM-5000", name: "Serie Mil Oficio", size: "48 × 35 × 25 cm", weight: "25 kg con docs", format: "Oficio" },
      { model: "AM-7000", name: "Serie Mil Archiversátil®", size: "62.3 × 36 × 25 cm", weight: "31.1 kg con docs", format: "Oficio y Carta" },
      { model: "AM-9000", name: "Serie Mil Registrador Archiversátil®", size: "65.5 × 36 × 28.2 cm", weight: "23.6 kg con docs", format: "Oficio y Carta" },
    ]
  },
  {
    gamma: "media",
    name: "REFORZADO",
    tagline: "La más versátil con la mejor relación resistencia/precio",
    color: "#F57C00",
    badge: "POPULAR",
    ect: "29 lb/pulg",
    estiba: 4,
    material: "Cartón corrugado Kraft, Flauta C",
    features: ["Sistema A.I.R. TC®", "Triple pared lateral", "Doble fondo", "Tapa integrada", "Econo-Pak 20 pzas"],
    products: [
      { model: "AM-02", name: "Reforzado Media Carta", size: "50 × 23 × 16 cm", format: "½ Carta" },
      { model: "AM-03", name: "Reforzado Mixta", size: "36 × 31.5 × 25 cm", weight: "19 kg con docs", format: "Oficio o Carta" },
      { model: "AM-04", name: "Reforzado Carta", size: "48 × 31 × 25 cm", weight: "20 kg con docs", format: "Carta" },
      { model: "AM-05", name: "Reforzado Oficio", size: "48 × 35 × 25 cm", weight: "25 kg con docs", format: "Oficio" },
      { model: "AM-07", name: "Archiversátil®", size: "62.3 × 36 × 25 cm", weight: "31.1 kg con docs", format: "Oficio y Carta" },
      { model: "AM-09", name: "Registrador Archiversátil®", size: "65.5 × 36 × 28.2 cm", weight: "23.6 kg con docs", format: "Oficio y Carta" },
    ]
  },
  {
    gamma: "baja",
    name: "KARTO-ARCHIVO®",
    tagline: "Para que archivar ya no te cueste más®",
    color: "#5D4037",
    badge: "ECONÓMICO",
    ect: "20-23 lb/pulg",
    estiba: 2,
    material: "Cartón corrugado Kraft, Flauta C",
    features: ["Diseño original patentado (1967)", "Triple pared lateral", "Doble fondo", "Tapa integrada", "Econo-Pak 20 pzas"],
    products: [
      { model: "KAC-40", name: "Karto-Archivo Carta", size: "48 × 31 × 25 cm", format: "Carta" },
      { model: "KAC-50", name: "Karto-Archivo Oficio", size: "48 × 35 × 25 cm", format: "Oficio" },
    ]
  }
];

const CatalogPage = () => {
  const [selectedGamma, setSelectedGamma] = useState("all");

  const filtered = selectedGamma === "all" ? PRODUCT_LINES : PRODUCT_LINES.filter(l => l.gamma === selectedGamma);

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FAF9F6]" data-testid="catalog-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Hero */}
        <div className="text-center mb-12">
          <span className="font-mono text-xs font-bold text-[#F57C00] tracking-[0.2em] uppercase">CATÁLOGO OFICIAL</span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#5D4037] mt-3">
            Familia ARCHIVO MUERTO<sup>®</sup>
          </h1>
          <p className="text-stone-600 text-lg mt-4 max-w-3xl mx-auto">
            Cajas de alta especialización para archivo de concentración. Diseño patentado desde 1967.
            <span className="block mt-2 font-semibold text-[#5D4037]">"Cajas de Archivo hay muchas... ARCHIVO MUERTO® solo hay uno y es TC todo de cartón®"</span>
          </p>
        </div>

        {/* Eco Badge */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-3 bg-[#2E7D32]/10 px-6 py-3 rounded-full">
            <Shield size={20} className="text-[#2E7D32]" />
            <span className="text-sm font-semibold text-[#2E7D32]">100%R® — Reciclable · Reciclado · Biodegradable · Tintas libres de plomo</span>
          </div>
        </div>

        {/* Gamma Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {[
            { value: "all", label: "Todas las Gamas" },
            { value: "alta", label: "Gama Alta — Serie Mil®", color: "#2E7D32" },
            { value: "media", label: "Gama Media — Reforzado", color: "#F57C00" },
            { value: "baja", label: "Gama Baja — Karto-Archivo®", color: "#5D4037" },
          ].map((f) => (
            <button key={f.value} onClick={() => setSelectedGamma(f.value)} data-testid={`filter-${f.value}`}
              className={`px-5 py-3 rounded-full font-medium transition-all text-sm ${selectedGamma === f.value ? "text-white shadow-lg" : "bg-white text-stone-600 hover:bg-stone-100 border border-stone-200"}`}
              style={selectedGamma === f.value ? { backgroundColor: f.color || "#5D4037" } : {}}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Product Lines */}
        <div className="space-y-12">
          {filtered.map((line) => (
            <motion.div key={line.gamma} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bento-card overflow-hidden" data-testid={`line-${line.gamma}`}>
              {/* Line Header */}
              <div className="p-6 md:p-8 text-white" style={{ backgroundColor: line.color }}>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <span className="text-xs font-mono tracking-wider text-white/70 uppercase">Gama {line.gamma === "alta" ? "Alta" : line.gamma === "media" ? "Media" : "Baja"}</span>
                    <h2 className="text-2xl md:text-3xl font-black mt-1">ARCHIVO MUERTO® {line.name}</h2>
                    <p className="text-white/80 mt-1">{line.tagline}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="bg-white/20 rounded-xl px-4 py-2 text-center backdrop-blur-sm">
                      <p className="text-2xl font-black">{line.estiba}</p>
                      <p className="text-xs text-white/80">Estiba garantizada</p>
                    </div>
                    <div className="bg-white/20 rounded-xl px-4 py-2 text-center backdrop-blur-sm">
                      <p className="text-lg font-bold">{line.ect}</p>
                      <p className="text-xs text-white/80">ECT Rating</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="px-6 md:px-8 py-4 bg-stone-50 border-b border-stone-200">
                <div className="flex flex-wrap gap-2">
                  {line.features.map((f, i) => (
                    <span key={i} className="flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-white border border-stone-200 text-stone-700">
                      <CheckCircle size={12} className="text-[#2E7D32]" /> {f}
                    </span>
                  ))}
                  <span className="text-xs px-3 py-1 rounded-full bg-stone-100 text-stone-500">{line.material}</span>
                </div>
              </div>

              {/* Products Grid */}
              <div className="p-6 md:p-8">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {line.products.map((product) => (
                    <div key={product.model} className="border border-stone-200 rounded-xl p-5 hover:border-stone-400 transition-colors group" data-testid={`product-${product.model}`}>
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-mono text-xs font-bold px-2 py-0.5 rounded" style={{ backgroundColor: `${line.color}15`, color: line.color }}>{product.model}</span>
                        <span className="text-xs text-stone-400">{product.format}</span>
                      </div>
                      <h3 className="font-bold text-[#5D4037] mb-2">{product.name}</h3>
                      <div className="space-y-1 text-sm text-stone-600">
                        <div className="flex items-center gap-2">
                          <Box size={14} className="text-stone-400" />
                          <span>{product.size}</span>
                        </div>
                        {product.weight && (
                          <div className="flex items-center gap-2">
                            <Package size={14} className="text-stone-400" />
                            <span>{product.weight}</span>
                          </div>
                        )}
                      </div>
                      <Link to="/contacto" className="mt-4 text-sm font-semibold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: line.color }}>
                        Solicitar cotización <ArrowRight size={14} />
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Comparison Table */}
        <div className="mt-12 bento-card p-6 md:p-8 overflow-x-auto">
          <h3 className="text-xl font-bold text-[#5D4037] mb-6">Comparativa de Gamas</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-200">
                <th className="text-left py-3 pr-4 text-stone-500 font-semibold">Característica</th>
                <th className="text-center py-3 px-4"><span className="font-bold text-[#5D4037]">Karto-Archivo®</span><br/><span className="text-xs text-stone-400">Gama Baja</span></th>
                <th className="text-center py-3 px-4"><span className="font-bold text-[#F57C00]">Reforzado</span><br/><span className="text-xs text-stone-400">Gama Media</span></th>
                <th className="text-center py-3 px-4"><span className="font-bold text-[#2E7D32]">Serie Mil®</span><br/><span className="text-xs text-stone-400">Gama Alta</span></th>
              </tr>
            </thead>
            <tbody className="text-stone-700">
              {[
                { label: "ECT Rating", baja: "20-23 lb/pulg", media: "29 lb/pulg", alta: "40 lb/pulg" },
                { label: "Estiba garantizada", baja: "2 cajas", media: "4 cajas", alta: "8 cajas (220+ kg)" },
                { label: "Triple pared lateral", baja: "✓", media: "✓", alta: "✓" },
                { label: "Doble fondo", baja: "✓", media: "✓", alta: "✓" },
                { label: "Tapa integrada", baja: "✓", media: "✓", alta: "✓ Doble ancho" },
                { label: "A.I.R. TC® Reforzado", baja: "—", media: "✓", alta: "✓" },
                { label: "Super-Estructura H®", baja: "—", media: "—", alta: "✓" },
                { label: "ID Visual TC®", baja: "—", media: "—", alta: "✓ (6 colores)" },
                { label: "Modelos disponibles", baja: "2", media: "6", alta: "4" },
                { label: "Presentación", baja: "Econo-Pak 20", media: "Individual / Econo-Pak 20", alta: "Individual Blister / Econo-Pak 15" },
              ].map((row, i) => (
                <tr key={i} className="border-b border-stone-100">
                  <td className="py-3 pr-4 font-medium">{row.label}</td>
                  <td className="py-3 px-4 text-center">{row.baja}</td>
                  <td className="py-3 px-4 text-center">{row.media}</td>
                  <td className="py-3 px-4 text-center font-semibold">{row.alta}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Heritage & CTA */}
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          <div className="bento-card p-8 bg-[#5D4037] text-white">
            <span className="font-mono text-xs tracking-wider text-white/60">DESDE 1967</span>
            <h3 className="text-2xl font-black mt-2">Diseño patentado original</h3>
            <p className="text-white/80 mt-3">Más de 50 años de innovación en cajas de archivo. Diseño de una sola pieza, imposible de desfondar, sin herramientas ni adhesivos. La referencia del mercado mexicano.</p>
            <div className="flex items-center gap-2 mt-4">
              <Star size={16} className="text-[#F57C00] fill-[#F57C00]" />
              <span className="text-sm">31 marcas registradas · PI valuada en $62M</span>
            </div>
          </div>
          <div className="bento-card p-8 flex flex-col justify-center">
            <h3 className="text-xl font-bold text-[#5D4037]">¿Necesitas cotización por volumen?</h3>
            <p className="text-stone-600 mt-2">Atendemos distribuidores, gobierno, corporativos y centros de archivo. Precios especiales por volumen.</p>
            <div className="flex gap-3 mt-6">
              <Link to="/contacto" className="btn-primary flex items-center gap-2 text-sm"><Phone size={16} /> Cotizar</Link>
              <Link to="/archivo-digital" className="btn-secondary flex items-center gap-2 text-sm"><Layers size={16} /> Servicio Digital</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CatalogPage;
